import React from 'react';
import classes from './AwaitingPayment.module.scss';
import { ArrowRightOutlined, SwapOutlined, HourglassOutlined, CloseOutlined } from '@ant-design/icons';
import { Button, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import RowLoader from 'components/Sceletons/RowLoader';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { fetchRoom } from 'store/reducers/roomsCreator';
import { setRooms } from 'store/slices/userDataSlice';
import billing, { paymentUrl } from 'api/billing';
import { clearReduxFunction } from 'store/reducers/userDataCreator';
import api from 'api';
import { checkIsTariffEnded, checkIsAwaitingPayment } from 'helpers/tariffPayment';
import { IDataRoom } from 'interfaces';
import PlanDetailsCard from 'components/PlanDetailsCard/PlanDetailsCard';

const paymentType = window.REACT_APP_PAYMENT_TYPE !== 'REPLACE_REACT_APP_PAYMENT_TYPE' ? window.REACT_APP_PAYMENT_TYPE : process.env.REACT_APP_PAYMENT_TYPE || 'jusan';

// const getTariffProfileName = (tariff: IDataRoom['tariff']) => {
//  if(tariff?.tariff_name === 'profile') {
//   return tariff.type;
//  }
//  return tariff?.tariff_name;
// }

const AwaitingPayment = () => {
  const [isRefusingTariff, setIsRefusingTariff] = React.useState(false);
  const [isPayingTariff, setIsPayingTariff] = React.useState(false);

  const { dataRoom, isLoadingRoom } = useAppSelector((store) => store.dataRoom);
  const { isLoadingUserData, available_rooms } = useAppSelector(
    (store) => store.userData
  );
  const { messageApi } = useAppSelector((store) => store.windowState);

  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isLoading = isLoadingRoom || isLoadingUserData;
  const hasPaymentLink = Boolean(dataRoom?.tariff?.last_payment_intent?.link);

  React.useEffect(() => {
    params.room && dispatch(fetchRoom({ id: params.room, navigate }));
  }, []);

  React.useEffect(() => {
    if (dataRoom && !checkIsAwaitingPayment(dataRoom)) {
      if (checkIsTariffEnded(dataRoom)) {
        navigate(`/room/${dataRoom?.id}/endedTariff`);
      } else {
        navigate(`/room/${dataRoom?.id}/documents`);
      }
    }
  }, [dataRoom]);

  const navigateToTariffs = () => {
    if (!isLoading) {
      navigate(`/tariffs`);
      dataRoom?.id && localStorage.setItem('ended_tariff_lifetime', dataRoom.id);
    }
  };

  const refuseTariff = async () => {
    setIsRefusingTariff(true);
    try {
      if (dataRoom) {
        await api.rejectTariff({billing_id: dataRoom.tariff?.last_payment_intent?.id!});
        const filteredRooms = available_rooms.filter((available_room) => available_room.id !== dataRoom.id);
        document.getElementById(`room_card_${dataRoom.id}`)?.classList.toggle(classes.slickHide);

        dispatch(clearReduxFunction({}));
        dataRoom?.id && navigate('/rooms');

        setTimeout(() => {
          dispatch(setRooms(filteredRooms));
        }, 400);
      };
      
    } catch (e) {
      messageApi?.error(t('Billing.error.declinePayment'));
    } finally {
      setIsRefusingTariff(false);
    }
  };
  const reRegisterIOSPayment = async (room: IDataRoom) => {
    const tariffJSON = {
      room_id: room.id,
      tariff_id: room?.tariff?.id,
      cost: room?.tariff?.cost,
      constraints: room?.tariff?.constraints,
      payment_type: paymentType,
    };
    try {
      const { data: paymentData } = await billing.registerBill(tariffJSON);
      return paymentData;
    } catch (e) {
      messageApi?.error(t('Header.error.register'));
    };
  };

  const payTheTariff = async () => {
    setIsPayingTariff(true);
    let paramsJSON: any;
    if (dataRoom?.tariff?.last_payment_intent?.payment_service === 'ios_iap') {
      paramsJSON = await reRegisterIOSPayment(dataRoom);
    } else {
      paramsJSON = dataRoom?.tariff?.last_payment_intent?.link && JSON.parse(dataRoom?.tariff?.last_payment_intent?.link);
    };
    console.log(paramsJSON);
    if (['robokassa', 'ios_iap'].includes(dataRoom?.tariff?.last_payment_intent?.payment_service)) {
      window.location.href = paramsJSON?.payment_link;
    } else {
      let qveryParams: string[] = [];
      for (const key in paramsJSON) {
        const value = paramsJSON[key] ? paramsJSON[key] : '';
        qveryParams.push(`${key}=${value}`);
      };
      const correctParams = qveryParams.join('&').replace('&redirect=true', '%26redirect=true');
      window.location.href = `${paymentUrl}?${correctParams}`;
    };
    setIsPayingTariff(false);
  };

  return (
    <div className={classes.page}>
      <div className={classes.card}>
        <div className={classes.iconWrap}>
          <div className={classes.iconCircle}>
            <HourglassOutlined className={classes.icon} />
          </div>
        </div>

        <Tag className={classes.statusTag}>
          <span className={classes.statusDot} />
          {t('tariffStatus.paymentPending')}
        </Tag>

        {isLoading ? (
          <RowLoader width='320px' padding='0' height='32px' />
        ) : (
          <h1 className={classes.title}>{dataRoom?.name}</h1>
        )}

        <p className={classes.description}>{t('awaitingPayment.text')}</p>

        <PlanDetailsCard room={dataRoom} isLoading={isLoading} />

        <div className={classes.actions}>
          {isLoading ? (
            <RowLoader width='180px' padding='0' height='36px' />
          ) : (
            hasPaymentLink && (
              <Button
                type='primary'
                size='large'
                loading={isPayingTariff}
                onClick={payTheTariff}
                icon={<ArrowRightOutlined />}
                iconPosition='end'
              >
                {t('awaitingPayment.pay')}
              </Button>
            )
          )}

          {isLoading ? (
            <RowLoader width='180px' padding='0' height='36px' />
          ) : (
            <Button
              size='large'
              onClick={navigateToTariffs}
              icon={<SwapOutlined />}
            >
              {t('awaitingPayment.change')}
            </Button>
          )}
        </div>

        {!isLoading && (
          <Button
            type='text'
            loading={isRefusingTariff}
            onClick={refuseTariff}
            icon={<CloseOutlined />}
            className={classes.cancelButton}
          >
            {t('awaitingPayment.reject')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AwaitingPayment;
